import { satisfiesRange, validateExpression } from '@ayumia/core/expressions';
import { isPrimitiveName, primitiveDefinition, VISUAL_PROPERTIES } from './primitives.js';
import { validatePropertyValue, propertyExpressions } from './properties.js';
import { ACTION_ROOTS, collectReferences, CONTEXT_KEYS, parseReference, PROPERTY_ROOTS, VARIABLE_ROOTS } from './scope.js';
import { parseService, SHELL_SERVICES } from './services.js';
import { codeBytes, RUN_CONTEXT_VERSIONS, RUN_LIMITS } from './run/sandbox.js';
/**
 * Gate sémantique d'un manifeste de composant (58 §4, §17, §18 ; ADR-048).
 *
 * Le contrat `component-manifest` dit la forme ; ces règles disent la cohérence, et valent autant
 * pour un manifeste écrit à la main que pour un manifeste produit par un agent (58 §18) :
 * références vers des racines permises et des noms déclarés, variables sans cycle ni fuite entre
 * composants, `set` vers l'état local seulement, services `api` déclarés et connus, primitives et
 * propriétés du registre, composants publiés déclarés en dépendance. Depuis 1.1 (ADR-083), `run` est compilé par
 * l'interpréteur du bac à sable, borné en taille, et ne vise que l'état local déclaré.
 */
/** Version du runtime livrée par ce paquet (59 §18). */
export const RUNTIME_VERSION = '1.2.0';
/** Premier runtime qui porte le bac à sable de `run` : un manifeste qui l'utilise exclut les runtimes antérieurs. */
export const RUN_MIN_RUNTIME_VERSION = '1.1.0';
/** Premier runtime qui lit les manifestes de composants fournis par le runtime (ADR-099). */
export const REGISTRY_MIN_RUNTIME_VERSION = '1.2.0';
/** Versions majeures du contrat de composant que ce runtime sait exécuter. */
export const SUPPORTED_CONTRACT_MAJORS = new Set([1]);
export const MAX_TEMPLATE_DEPTH = 24;
export const MAX_TEMPLATE_NODES = 2000;
const VALUE_TYPE_CHECK = {
    string: (value) => typeof value === 'string',
    number: (value) => typeof value === 'number' && Number.isFinite(value),
    boolean: (value) => typeof value === 'boolean',
    array: (value) => Array.isArray(value),
    object: (value) => typeof value === 'object' && !Array.isArray(value),
};
export function validateManifest(manifest, options = {}) {
    const findings = [];
    const add = (path, code, message) => {
        findings.push({ path, code, message });
    };
    const exprOptions = (roots) => (options.functions === undefined ? { roots } : { roots, functions: options.functions });
    const major = Number(manifest.contract_version.split('.')[0]);
    if (!SUPPORTED_CONTRACT_MAJORS.has(major))
        add('/contract_version', 'CONTRACT_VERSION_UNSUPPORTED', `Contrat de composant ${manifest.contract_version} non pris en charge par le runtime ${RUNTIME_VERSION}.`);
    try {
        if (!satisfiesRange(RUNTIME_VERSION, manifest.runtime_compatibility)) {
            add('/runtime_compatibility', 'RUNTIME_INCOMPATIBLE', `Le runtime ${RUNTIME_VERSION} ne satisfait pas « ${manifest.runtime_compatibility} ».`);
        }
    }
    catch {
        add('/runtime_compatibility', 'RUNTIME_RANGE_INVALID', `« ${manifest.runtime_compatibility} » n'est pas une plage semver.`);
    }
    // 1.2 (ADR-099) : composant fourni par le runtime, entrées typées, contrôles du conteneur, états, exemples.
    const declarations = Object.values(manifest.inputs ?? {});
    const usesRegistry = manifest.implementation !== undefined ||
        manifest.container_features !== undefined ||
        manifest.states !== undefined ||
        manifest.examples !== undefined ||
        manifest.accepts_children !== undefined ||
        declarations.some((declaration) => declaration.schema !== undefined || declaration.enum !== undefined || declaration.type === 'component');
    if (usesRegistry && major === 1 && Number(manifest.contract_version.split('.')[1] ?? '0') < 2) {
        add('/contract_version', 'REGISTRY_CONTRACT_VERSION', 'Un manifeste qui déclare implementation, schema, enum, le type component, container_features, states ou examples vise le contrat 1.2 (ADR-099).');
    }
    // 1.3 (ADR-146 §3.1) : un bloc de formulaire vise le contrat 1.3.
    if (manifest.block !== undefined && major === 1 && Number(manifest.contract_version.split('.')[1] ?? '0') < 3) {
        add('/contract_version', 'BLOCK_CONTRACT_VERSION', 'Un manifeste qui déclare un bloc de formulaire vise le contrat 1.3 (ADR-146).');
    }
    if (manifest.implementation !== undefined && manifest.component.category !== 'core') {
        add('/implementation', 'IMPLEMENTATION_NOT_CORE', 'Seul un composant Core est fourni par le runtime ; un Pack ou un client publie des composites (59 §2.3).');
    }
    const inputs = new Set(Object.keys(manifest.inputs ?? {}));
    const state = new Set(Object.keys(manifest.state ?? {}));
    const variables = new Set(Object.keys(manifest.variables ?? {}));
    const outputs = manifest.outputs ?? {};
    const tokens = new Set(manifest.theme?.required_tokens ?? []);
    const capabilities = new Set(manifest.capabilities ?? []);
    const dependencies = new Set((manifest.dependencies ?? []).map((dependency) => dependency.artifact_id));
    for (const [name, declaration] of Object.entries(manifest.inputs ?? {})) {
        if (declaration.default !== undefined)
            checkLiteralType(`/inputs/${name}/default`, declaration.type, declaration.default);
    }
    for (const [name, declaration] of Object.entries(manifest.state ?? {})) {
        if (declaration.initial !== undefined && declaration.initial !== null)
            checkLiteralType(`/state/${name}/initial`, declaration.type, declaration.initial);
    }
    function checkLiteralType(path, type, value) {
        const check = VALUE_TYPE_CHECK[type];
        if (check !== undefined && !check(value))
            add(path, 'VALUE_TYPE_MISMATCH', `Valeur incompatible avec le type « ${type} ».`);
    }
    /** Une référence nomme une entrée, un état, une variable, un jeton ou une clé de contexte déclarés. */
    const checkReference = (reference, path) => {
        const [first] = reference.segments;
        const declared = (set, what) => {
            if (first === undefined)
                add(path, 'REFERENCE_TOO_BROAD', `$${reference.root} se lit par un nom : $${reference.root}.<nom>.`);
            else if (!set.has(first))
                add(path, 'REFERENCE_UNDECLARED', `« ${first} » n'est pas ${what} déclaré(e) de ce composant.`);
        };
        switch (reference.root) {
            case 'inputs':
                declared(inputs, 'une entrée');
                break;
            case 'state':
                declared(state, 'un état');
                break;
            case 'variables':
                declared(variables, 'une variable');
                break;
            case 'context':
                declared(CONTEXT_KEYS, 'une clé de contexte');
                break;
            case 'theme': {
                const token = reference.segments.join('.');
                if (!tokens.has(token))
                    add(path, 'THEME_TOKEN_UNDECLARED', `Le jeton « ${token} » n'est pas dans theme.required_tokens.`);
                break;
            }
            default:
                break;
        }
    };
    const checkExpression = (expression, roots, path) => {
        const shape = validateExpression(expression, exprOptions(roots));
        for (const finding of shape)
            add(path, finding.code, finding.message);
        if (shape.length === 0)
            for (const reference of collectReferences(expression))
                checkReference(reference, path);
    };
    // Variables : racines permises, noms déclarés, aucun cycle (58 §7).
    for (const [name, expression] of Object.entries(manifest.variables ?? {}))
        checkExpression(expression, VARIABLE_ROOTS, `/variables/${name}`);
    const cycle = variableCycle(manifest.variables ?? {});
    if (cycle !== null)
        add(`/variables/${cycle[0]}`, 'VARIABLE_CYCLE', `Cycle de variables : ${cycle.join(' → ')}.`);
    // Gabarit.
    let nodes = 0;
    const visit = (node, path, depth, inRepeat) => {
        nodes += 1;
        if (depth > MAX_TEMPLATE_DEPTH) {
            add(path, 'TEMPLATE_TOO_DEEP', `Profondeur maximale du gabarit : ${MAX_TEMPLATE_DEPTH}.`);
            return;
        }
        if (nodes === MAX_TEMPLATE_NODES + 1)
            add(path, 'TEMPLATE_TOO_LARGE', `Au plus ${MAX_TEMPLATE_NODES} nœuds.`);
        if (nodes > MAX_TEMPLATE_NODES)
            return;
        const repeatRoots = inRepeat || node.repeat !== undefined;
        if (node.repeat !== undefined)
            checkExpression(node.repeat.items, inRepeat ? [...PROPERTY_ROOTS, 'item'] : PROPERTY_ROOTS, `${path}/repeat/items`);
        const propertyRoots = repeatRoots ? [...PROPERTY_ROOTS, 'item'] : PROPERTY_ROOTS;
        const actionRoots = repeatRoots ? [...ACTION_ROOTS, 'item'] : ACTION_ROOTS;
        let allowedProps = null;
        let allowedEvents = null;
        let acceptsChildren = true;
        if (isPrimitiveName(node.component)) {
            const definition = primitiveDefinition(node.component);
            if (definition === undefined)
                add(`${path}/component`, 'PRIMITIVE_UNKNOWN', `« ${node.component} » n'est pas une primitive du registre.`);
            else {
                allowedProps = definition.props;
                allowedEvents = definition.events;
                acceptsChildren = definition.children;
            }
        }
        else {
            if (!dependencies.has(node.component))
                add(`${path}/component`, 'COMPONENT_NOT_DECLARED', `« ${node.component} » est utilisé sans figurer dans dependencies.`);
            acceptsChildren = false;
            const child = options.resolveComponent?.(node.component);
            if (child !== undefined) {
                allowedProps = new Set(Object.keys(child.inputs ?? {}));
                allowedEvents = new Set(Object.keys(child.outputs ?? {}));
                for (const [inputName, declaration] of Object.entries(child.inputs ?? {})) {
                    if (declaration.required === true && declaration.default === undefined && node.props?.[inputName] === undefined) {
                        add(`${path}/props`, 'COMPONENT_INPUT_MISSING', `L'entrée obligatoire « ${inputName} » de ${node.component} n'est pas fournie.`);
                    }
                }
            }
        }
        if (!acceptsChildren && (node.children ?? []).length > 0)
            add(`${path}/children`, 'CHILDREN_NOT_ALLOWED', `« ${node.component} » n'accepte pas d'enfants.`);
        for (const [name, value] of Object.entries(node.props ?? {})) {
            const at = `${path}/props/${name}`;
            if (allowedProps !== null && !allowedProps.has(name))
                add(at, 'PROPERTY_UNKNOWN', `« ${name} » n'est pas une propriété de ${node.component}.`);
            checkProperty(value, propertyRoots, at);
        }
        for (const [name, value] of Object.entries(node.style ?? {})) {
            const at = `${path}/style/${name}`;
            if (!VISUAL_PROPERTIES.has(name))
                add(at, 'STYLE_PROPERTY_UNKNOWN', `« ${name} » n'est pas une propriété visuelle (58 §11) ; aucun CSS libre.`);
            checkProperty(value, propertyRoots, at);
        }
        for (const [event, handler] of Object.entries(node.events ?? {})) {
            const at = `${path}/events/${event}`;
            if (allowedEvents !== null && !allowedEvents.has(event))
                add(at, 'EVENT_UNKNOWN', `« ${node.component} » n'émet pas « ${event} ».`);
            handler.actions.forEach((action, index) => checkAction(action, actionRoots, `${at}/actions/${index}`));
        }
        const keys = new Set();
        (node.children ?? []).forEach((child, index) => {
            if (child.key !== undefined) {
                if (keys.has(child.key))
                    add(`${path}/children/${index}/key`, 'KEY_DUPLICATE', `La clé « ${child.key} » est déjà utilisée par un frère.`);
                keys.add(child.key);
            }
            visit(child, `${path}/children/${index}`, depth + 1, repeatRoots);
        });
    };
    const checkProperty = (value, roots, path) => {
        const shape = validatePropertyValue(value, roots, options.functions);
        for (const finding of shape)
            add(`${path}${finding.path}`, finding.code, finding.message);
        if (shape.length === 0) {
            for (const { expression } of propertyExpressions(value))
                for (const reference of collectReferences(expression))
                    checkReference(reference, path);
        }
    };
    const checkAction = (action, roots, path) => {
        if (action.action === 'run') {
            checkRun(action, roots, path);
            return;
        }
        if (action.action === 'set') {
            const target = parseReference(action.target);
            if (target === null || target.root !== 'state')
                add(`${path}/target`, 'SET_TARGET_NOT_STATE', 'set ne modifie que l’état local ($state.<nom>) ; une donnée métier passe par api.');
            else
                checkReference(target, `${path}/target`);
            checkExpression(action.value, roots, `${path}/value`);
            return;
        }
        const service = parseService(action.service);
        if (service === null)
            add(`${path}/service`, 'SERVICE_UNKNOWN', `« ${action.service} » n'est ni une commande, ni une requête, ni un service de coquille.`);
        else if (!capabilities.has(action.service))
            add(`${path}/service`, 'SERVICE_NOT_DECLARED', `« ${action.service} » doit figurer dans capabilities.`);
        const params = action.params ?? {};
        for (const [name, expression] of Object.entries(params))
            checkExpression(expression, roots, `${path}/params/${name}`);
        if (service !== null && (service.kind === 'shell' || service.kind === 'emit')) {
            const definition = SHELL_SERVICES[action.service];
            for (const required of definition.required)
                if (!(required in params))
                    add(`${path}/params`, 'SERVICE_PARAM_MISSING', `${action.service} attend « ${required} ».`);
            if (definition.optional !== null) {
                const allowed = new Set([...definition.required, ...definition.optional]);
                for (const name of Object.keys(params))
                    if (!allowed.has(name))
                        add(`${path}/params/${name}`, 'SERVICE_PARAM_UNKNOWN', `${action.service} n'accepte pas « ${name} ».`);
            }
            if (service.kind === 'emit') {
                const output = params['output'];
                if (typeof output !== 'string' || output.startsWith('$'))
                    add(`${path}/params/output`, 'EMIT_OUTPUT_NOT_LITERAL', 'component.emit nomme sa sortie par un littéral.');
                else if (!Object.hasOwn(outputs, output))
                    add(`${path}/params/output`, 'EMIT_OUTPUT_UNDECLARED', `La sortie « ${output} » n'est pas déclarée dans outputs.`);
                else {
                    const payload = new Set(Object.keys(outputs[output].payload ?? {}));
                    for (const name of Object.keys(params))
                        if (name !== 'output' && !payload.has(name))
                            add(`${path}/params/${name}`, 'EMIT_PAYLOAD_UNDECLARED', `« ${name} » n'est pas dans la charge utile de « ${output} ».`);
                }
            }
        }
        if (action.result_to !== undefined) {
            const target = parseReference(action.result_to);
            if (target === null || target.root !== 'state')
                add(`${path}/result_to`, 'SET_TARGET_NOT_STATE', 'result_to vise l’état local.');
            else
                checkReference(target, `${path}/result_to`);
        }
        (action.on_success ?? []).forEach((next, index) => checkAction(next, roots, `${path}/on_success/${index}`));
        (action.on_error ?? []).forEach((next, index) => checkAction(next, roots, `${path}/on_error/${index}`));
    };
    let runSeen = false;
    const checkRun = (action, roots, path) => {
        if (!runSeen) {
            runSeen = true;
            const minor = Number(manifest.contract_version.split('.')[1] ?? '0');
            if (major === 1 && minor < 1)
                add('/contract_version', 'RUN_CONTRACT_VERSION', 'Un manifeste qui utilise run vise le contrat 1.1 (ADR-083).');
            try {
                if (satisfiesRange('1.0.0', manifest.runtime_compatibility) || satisfiesRange('1.0.999', manifest.runtime_compatibility)) {
                    add('/runtime_compatibility', 'RUN_RUNTIME_COMPATIBILITY', `Un manifeste qui utilise run exclut les runtimes sans bac à sable : « >=${RUN_MIN_RUNTIME_VERSION} » au moins (ADR-083 §4).`);
                }
            }
            catch {
                // plage invalide : déjà signalée
            }
        }
        if (!RUN_CONTEXT_VERSIONS.has(action.context_version))
            add(`${path}/context_version`, 'RUN_CONTEXT_VERSION_UNKNOWN', `Contexte d'exécution « ${action.context_version} » inconnu du runtime ${RUNTIME_VERSION}.`);
        const bytes = codeBytes(action.code);
        if (bytes > RUN_LIMITS.code_bytes)
            add(`${path}/code`, 'RUN_CODE_TOO_LARGE', `Code de ${bytes} octets : ${RUN_LIMITS.code_bytes} au plus.`);
        else if (options.compileRun === undefined)
            add(`${path}/code`, 'RUN_COMPILER_UNAVAILABLE', 'Le code de run se valide par l’interpréteur du bac à sable : aucun compilateur fourni.');
        else {
            const error = options.compileRun(action.code);
            if (error !== null)
                add(`${path}/code`, 'RUN_CODE_INVALID', error);
        }
        if ((action.timeout_ms ?? RUN_LIMITS.default_timeout_ms) > RUN_LIMITS.max_timeout_ms)
            add(`${path}/timeout_ms`, 'RUN_TIMEOUT_TOO_LONG', `${RUN_LIMITS.max_timeout_ms} ms au plus.`);
        checkReturnsShape(action.returns, `${path}/returns`);
        if (action.target !== undefined) {
            const target = parseReference(action.target);
            if (target === null || target.root !== 'state')
                add(`${path}/target`, 'RUN_FORBIDDEN_TARGET', 'run n’écrit que dans l’état local ($state.<nom>) ; une donnée métier passe par api.');
            else
                checkReference(target, `${path}/target`);
        }
        (action.on_error ?? []).forEach((next, index) => checkAction(next, roots, `${path}/on_error/${index}`));
    };
    const checkReturnsShape = (returns, path) => {
        const types = new Set(Array.isArray(returns.type) ? returns.type : [returns.type]);
        if (returns.items !== undefined) {
            if (!types.has('array'))
                add(`${path}/items`, 'RUN_RETURNS_INVALID', 'items ne vaut que pour le type array.');
            checkReturnsShape(returns.items, `${path}/items`);
        }
        if (returns.properties !== undefined || returns.required !== undefined) {
            if (!types.has('object'))
                add(path, 'RUN_RETURNS_INVALID', 'properties et required ne valent que pour le type object.');
            for (const name of returns.required ?? [])
                if (returns.properties !== undefined && !Object.hasOwn(returns.properties, name))
                    add(`${path}/required`, 'RUN_RETURNS_INVALID', `« ${name} » est requis sans être décrit dans properties.`);
            for (const [name, child] of Object.entries(returns.properties ?? {}))
                checkReturnsShape(child, `${path}/properties/${name}`);
        }
    };
    if (manifest.template !== undefined)
        visit(manifest.template, '/template', 0, false);
    else if (manifest.implementation === undefined)
        add('/template', 'TEMPLATE_MISSING', 'Un composite déclare son gabarit ; seul un composant fourni par le runtime (implementation) en est dispensé.');
    for (const capability of capabilities) {
        if (parseService(capability) === null)
            add('/capabilities', 'SERVICE_UNKNOWN', `« ${capability} » n'est pas un service connu.`);
    }
    return findings;
}
/** Premier cycle trouvé entre variables, ou `null`. */
function variableCycle(variables) {
    const edges = new Map();
    for (const [name, expression] of Object.entries(variables)) {
        edges.set(name, collectReferences(expression)
            .filter((reference) => reference.root === 'variables' && reference.segments[0] !== undefined)
            .map((reference) => reference.segments[0]));
    }
    const visiting = [];
    const done = new Set();
    const walk = (name) => {
        const index = visiting.indexOf(name);
        if (index >= 0)
            return [...visiting.slice(index), name];
        if (done.has(name) || !edges.has(name))
            return null;
        visiting.push(name);
        for (const next of edges.get(name)) {
            const found = walk(next);
            if (found !== null)
                return found;
        }
        visiting.pop();
        done.add(name);
        return null;
    };
    for (const name of edges.keys()) {
        const found = walk(name);
        if (found !== null)
            return found;
    }
    return null;
}
/** Ordre d'évaluation des variables : dépendances d'abord. Suppose l'absence de cycle. */
export function variableOrder(variables) {
    const order = [];
    const done = new Set();
    const walk = (name) => {
        if (done.has(name) || !Object.hasOwn(variables, name))
            return;
        done.add(name);
        for (const reference of collectReferences(variables[name])) {
            if (reference.root === 'variables' && reference.segments[0] !== undefined)
                walk(reference.segments[0]);
        }
        order.push(name);
    };
    for (const name of Object.keys(variables))
        walk(name);
    return order;
}
//# sourceMappingURL=validate-manifest.js.map